import type { ReactNode } from "react";
import type { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

// Generic over the caller's form values (zod-inferred): `form` keeps its
// UseFormReturn type and `name` narrows to that form's field names, so
// call sites get field-name completion and typo errors.
interface MyFormFieldBaseProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: FieldPath<T>;
  label?: ReactNode;
  description?: ReactNode;
  placeholder?: string;
  className?: string;
}

export const MyFormInput = <T extends FieldValues>({
  form,
  name,
  label,
  description,
  placeholder,
  ...props
}: MyFormFieldBaseProps<T> & { disabled?: boolean; type?: string }) => {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input placeholder={placeholder} {...field} {...props} />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const MyFromTextarea = <T extends FieldValues>({
  form,
  name,
  placeholder,
  label,
  description,
  rows = 3,
  className,
}: MyFormFieldBaseProps<T> & { rows?: number }) => {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Textarea
              placeholder={placeholder}
              className={cn("resize-none", className)}
              {...field}
              rows={rows}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export interface MyFormSelectItem {
  code: string;
  name?: ReactNode;
  short?: ReactNode;
}

export const MyFormSelect = <T extends FieldValues>({
  form,
  items,
  name,
  label,
  description,
  placeholder,
  showCode,
  showShort,
  onChange,
}: MyFormFieldBaseProps<T> & {
  items: MyFormSelectItem[];
  showCode?: boolean;
  showShort?: boolean;
  onChange?: (value: string) => void;
}) => {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {label && <FormLabel>{label}</FormLabel>}
          <Select
            onValueChange={(value) => {
              field.onChange(value);
              if (onChange) onChange(value);
            }}
            defaultValue={field.value}
          >
            <FormControl>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {items.map((item) => (
                <SelectItem key={item.code} value={item.code}>
                  {showCode && <span>{item.code} - </span>}
                  {showShort ? (
                    <span>{item.short}</span>
                  ) : (
                    <span>{item.name}</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
